(function () {
  const normalize = (value) => (value || '').toLowerCase().replace(/\s+/g, '');

  class FindMyModel extends HTMLElement {
    constructor() {
      super();
      this.level = 1;
      this.activeBrandId = null;
      this.activeCollectionId = null;
      this.lastFocus = null;

      this.onDocClick = this.onDocClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onSearchSubmit = this.onSearchSubmit.bind(this);
      this.onSearchInput = this.onSearchInput.bind(this);
      this.onInteract = this.onInteract.bind(this);
    }

    connectedCallback() {
      const existing = document.querySelector('find-my-model[data-fmm-primary]');
      if (existing && existing !== this) {
        this.remove();
        return;
      }
      this.setAttribute('data-fmm-primary', '');
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
      if (this._initialized) return;
      this._initialized = true;
      this.panel = this.querySelector('.find-my-model__panel');
      this.levels = this.querySelector('.find-my-model__levels');
      this.brandLevel = this.querySelector('.find-my-model__level--1');
      this.searchInput = this.querySelector('.find-my-model__search-input');
      this.searchForm = this.querySelector('[data-fmm-search-form]');
      this.currentBrandLabel = this.querySelector('[data-fmm-current-brand]');
      this.currentCollectionLabel = this.querySelector('[data-fmm-current-collection]');
      this.searchResultsList = this.querySelector('[data-fmm-search-results]');
      this.emptyState = this.querySelector('[data-fmm-empty]');
      this.seriesLists = this.querySelectorAll('[data-fmm-series-of]');
      this.productLists = this.querySelectorAll('[data-fmm-products-of]');
      this.brandItems = this.querySelectorAll('[data-fmm-brand]');
      this.collectionItems = this.querySelectorAll('[data-fmm-collection]');
      this.addEventListener('click', this.onInteract);
      if (this.searchForm) this.searchForm.addEventListener('submit', this.onSearchSubmit);
      if (this.searchInput) this.searchInput.addEventListener('input', this.onSearchInput);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeyDown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onInteract);
      if (this.searchForm) this.searchForm.removeEventListener('submit', this.onSearchSubmit);
      if (this.searchInput) this.searchInput.removeEventListener('input', this.onSearchInput);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeyDown);
    }

    onSearchSubmit(event) {
      event.preventDefault();
      this.runSearch();
    }

    onSearchInput() {
      if (this.searchInput.value === '') {
        this.clearSearch();
        if (this.activeCollectionId) {
          this.productLists.forEach((list) => {
            list.hidden = list.dataset.fmmProductsOf !== this.activeCollectionId;
          });
          this.setLevel(3);
        } else {
          this.setLevel(1);
        }
      }
    }

    onDocClick(event) {
      const trigger = event.target.closest('[data-find-my-model-open]');
      if (trigger) {
        event.preventDefault();
        if (this.dataset.state === 'open' && trigger === this.lastFocus) {
          this.close();
          return;
        }
        this.open(trigger);
        return;
      }

      if (
        this.dataset.state === 'open' &&
        this.dataset.presentation === 'popover' &&
        this.panel &&
        !this.panel.contains(event.target)
      ) {
        this.close();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.dataset.state === 'open') {
        this.close();
      }
    }

    onInteract(event) {
      const closeTarget = event.target.closest('[data-fmm-close]');
      if (closeTarget) {
        event.preventDefault();
        this.close();
        return;
      }
      const actionEl = event.target.closest('[data-fmm-action]');
      if (!actionEl || !this.contains(actionEl)) return;
      const action = actionEl.dataset.fmmAction;
      if (action === 'open-brand') {
        event.preventDefault();
        this.openBrand(actionEl.dataset.fmmTarget);
      } else if (action === 'open-collection') {
        event.preventDefault();
        this.openCollection(actionEl.dataset.fmmTarget);
      } else if (action === 'back') {
        event.preventDefault();
        this.goBack();
      }
    }

    open(trigger) {
      this.lastFocus = trigger || document.activeElement;
      this.dataset.presentation = trigger && trigger.dataset.fmmDisplay === 'popover' ? 'popover' : 'drawer';
      if (this.panel) {
        this.panel.setAttribute('aria-modal', this.dataset.presentation === 'drawer' ? 'true' : 'false');
      }
      this.dataset.state = 'open';
      if (this.dataset.presentation === 'drawer') {
        document.body.classList.add('find-my-model-open');
      } else {
        document.body.classList.remove('find-my-model-open');
      }
      if (this.lastFocus && this.lastFocus.setAttribute) {
        this.lastFocus.setAttribute('aria-expanded', 'true');
      }
      this.hydrateImages(this.brandLevel);
      requestAnimationFrame(() => {
        this.searchInput.focus();
      });
    }

    hydrateImages(root) {
      if (!root) return;
      root.querySelectorAll('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
    }

    close() {
      this.dataset.state = 'closed';
      document.body.classList.remove('find-my-model-open');
      if (this.lastFocus && this.lastFocus.setAttribute) {
        this.lastFocus.setAttribute('aria-expanded', 'false');
      }
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
      clearTimeout(this._resetTimer);
      this._resetTimer = setTimeout(() => this.resetToLevel1(), 400);
    }

    resetToLevel1() {
      this.searchInput.value = '';
      this.clearSearch();
      this.setLevel(1);
      this.activeBrandId = null;
      this.activeCollectionId = null;
      this.seriesLists.forEach((list) => { list.hidden = true; });
      this.productLists.forEach((list) => { list.hidden = true; });
    }

    setLevel(level) {
      this.level = level;
      this.levels.dataset.fmmLevel = String(level);
      const activeLevel = this.querySelector(`.find-my-model__level[data-level="${level}"]`);
      if (activeLevel) {
        activeLevel.scrollTop = 0;
      }
      this.querySelectorAll('.find-my-model__level').forEach((el) => {
        el.setAttribute('aria-hidden', el.dataset.level === String(level) ? 'false' : 'true');
      });
    }

    openBrand(id) {
      this.activeBrandId = id;
      this.activeCollectionId = null;
      this.seriesLists.forEach((list) => {
        const matches = list.dataset.fmmSeriesOf === id;
        list.hidden = !matches;
        if (matches) this.hydrateImages(list);
      });
      this.productLists.forEach((list) => {
        list.hidden = true;
      });
      if (this.searchResultsList) {
        this.searchResultsList.hidden = true;
        this.searchResultsList.innerHTML = '';
      }
      if (this.emptyState) this.emptyState.hidden = true;

      const brandItem = this.querySelector(`[data-fmm-brand="${CSS.escape(id)}"]`);
      const title = brandItem ? brandItem.querySelector('.find-my-model__row-label').textContent.trim() : '';
      if (this.currentBrandLabel) this.currentBrandLabel.textContent = title;
      this.brandItems.forEach((el) => {
        el.classList.toggle('is-active', el.dataset.fmmBrand === id);
      });
      this.setLevel(2);
    }

    openCollection(id) {
      this.activeCollectionId = id;
      this.productLists.forEach((list) => {
        const matches = list.dataset.fmmProductsOf === id;
        list.hidden = !matches;
        if (matches) {
          list.querySelectorAll('.find-my-model__item').forEach((item) => {
            item.dataset.fmmHidden = 'false';
          });
          this.hydrateImages(list);
        }
      });
      if (this.searchResultsList) {
        this.searchResultsList.hidden = true;
        this.searchResultsList.innerHTML = '';
      }
      if (this.emptyState) this.emptyState.hidden = true;

      const collectionItem = this.querySelector(`[data-fmm-collection="${CSS.escape(id)}"]`);
      const title = collectionItem ? collectionItem.querySelector('.find-my-model__row-label').textContent.trim() : '';
      if (this.currentCollectionLabel) this.currentCollectionLabel.textContent = title;
      this.collectionItems.forEach((el) => {
        el.classList.toggle('is-active', el.dataset.fmmCollection === id);
      });
      this.setLevel(3);
    }

    goBack() {
      if (this.isSearching()) {
        const hadCollection = !!this.activeCollectionId;
        this.searchInput.value = '';
        this.clearSearch();
        if (hadCollection) {
          this.productLists.forEach((list) => {
            list.hidden = list.dataset.fmmProductsOf !== this.activeCollectionId;
          });
          this.setLevel(3);
        } else {
          this.setLevel(1);
        }
        return;
      }
      if (this.level === 3) {
        this.setLevel(2);
      } else if (this.level === 2) {
        this.setLevel(1);
      }
    }

    isSearching() {
      return this.searchInput && this.searchInput.value.trim().length > 0;
    }

    clearSearch() {
      if (this.searchResultsList) {
        this.searchResultsList.hidden = true;
        this.searchResultsList.innerHTML = '';
      }
      if (this.emptyState) this.emptyState.hidden = true;
      this.productLists.forEach((list) => {
        list.querySelectorAll('.find-my-model__item').forEach((item) => {
          item.dataset.fmmHidden = 'false';
        });
      });
    }

    runSearch() {
      const query = normalize(this.searchInput.value);

      if (!query) {
        this.clearSearch();
        if (this.activeCollectionId) {
          this.productLists.forEach((list) => {
            list.hidden = list.dataset.fmmProductsOf !== this.activeCollectionId;
          });
          this.setLevel(3);
        } else {
          this.setLevel(1);
        }
        return;
      }

      this.setLevel(3);

      if (this.activeCollectionId) {
        const list = this.querySelector(`[data-fmm-products-of="${CSS.escape(this.activeCollectionId)}"]`);
        if (!list) return;
        list.hidden = false;
        if (this.searchResultsList) this.searchResultsList.hidden = true;
        let matches = 0;
        list.querySelectorAll('.find-my-model__item').forEach((item) => {
          const name = item.dataset.fmmName || '';
          const hit = name.includes(query);
          item.dataset.fmmHidden = hit ? 'false' : 'true';
          if (hit) matches += 1;
        });
        if (this.emptyState) this.emptyState.hidden = matches > 0;
        return;
      }

      this.productLists.forEach((list) => {
        list.hidden = true;
      });
      if (!this.searchResultsList) return;
      this.searchResultsList.hidden = false;
      this.searchResultsList.innerHTML = '';
      if (this.currentCollectionLabel) {
        this.currentCollectionLabel.textContent = (this.dataset.searchLabel || 'Search results').toUpperCase();
      }

      if (!this._searchIndex) this._searchIndex = this.buildSearchIndex();
      let matches = 0;
      const frag = document.createDocumentFragment();
      for (let i = 0; i < this._searchIndex.length; i++) {
        const entry = this._searchIndex[i];
        if (entry.name.includes(query)) {
          const clone = entry.el.cloneNode(true);
          clone.dataset.fmmHidden = 'false';
          frag.appendChild(clone);
          matches += 1;
        }
      }
      this.searchResultsList.appendChild(frag);
      if (this.emptyState) this.emptyState.hidden = matches > 0;
      this.hydrateImages(this.searchResultsList);
    }

    buildSearchIndex() {
      const seen = new Set();
      const index = [];
      this.productLists.forEach((list) => {
        list.querySelectorAll('.find-my-model__item').forEach((item) => {
          const id = item.dataset.fmmProduct || '';
          const name = item.dataset.fmmName || '';
          const key = id + '|' + name;
          if (seen.has(key)) return;
          seen.add(key);
          index.push({ name: name, el: item });
        });
      });
      return index;
    }
  }

  if (!customElements.get('find-my-model')) {
    customElements.define('find-my-model', FindMyModel);
  }
})();
